import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import Layout from './components/Layout';
import Login from './components/Login';
import Registration from './components/Registration';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import UserDashboard from './components/UserDashboard';
import AdminDashboard from './components/AdminDashboard';
import ExamPage from './components/ExamPage';
import ExamResults from './components/ExamResults';
import TechnicianProfile from './components/TechnicianProfile';
import ManagerDashboard from './components/ManagerDashboard';
import TeamMemberView from './components/TeamMemberView';
import RevenueManualEntry from './components/RevenueManualEntry';
import ProtectedRoute from './components/ProtectedRoute';
import TokenExpirationWarning from './components/TokenExpirationWarning';
import { AuthProvider, useAuth } from './context/AuthContext';

// Smart home route that redirects based on auth state
const HomeRoute: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  
  // Show loading indicator while authentication state is being initialized
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate replace to="/login" />;
  }
  
  return <Navigate replace to={user?.is_admin ? "/admin-dashboard" : "/user-dashboard"} />;
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <ErrorBoundary>
        <TokenExpirationWarning />
        <Layout>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Registration />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/user-dashboard" element={
              <ProtectedRoute>
                <UserDashboard />
              </ProtectedRoute>
            } />
            <Route path="/admin-dashboard" element={
              <ProtectedRoute adminOnly>
                <AdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/exam/:id" element={
              <ProtectedRoute>
                <ExamPage />
              </ProtectedRoute>
            } />
            <Route path="/exam-results" element={
              <ProtectedRoute>
                <ExamResults />
              </ProtectedRoute>
            } />
            <Route path="/technician/profile" element={
              <ProtectedRoute>
                <TechnicianProfile />
              </ProtectedRoute>
            } />
            <Route path="/manager/dashboard" element={
              <ProtectedRoute>
                <ManagerDashboard />
              </ProtectedRoute>
            } />
            <Route path="/manager/technician/:userId" element={
              <ProtectedRoute>
                <TeamMemberView />
              </ProtectedRoute>
            } />
            <Route path="/admin/technician/:userId" element={
              <ProtectedRoute adminOnly>
                <TeamMemberView />
              </ProtectedRoute>
            } />
            <Route path="/admin/revenue-entry" element={
              <ProtectedRoute adminOnly>
                <RevenueManualEntry />
              </ProtectedRoute>
            } />
            <Route path="/" element={<HomeRoute />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Layout>
        </ErrorBoundary>
      </AuthProvider>
    </Router>
  );
}

export default App;